import React, { useState } from "react";
import {
  Avatar, Typography, Space, Button, Tooltip,
  Badge, Dropdown, Drawer, Layout, Menu,
} from "antd";
import {
  Ship, Bell, User,
  Menu as MenuIcon, X, LogOut,
} from "lucide-react";
import Logo from "@/assets/logo.jpeg";
import { DownOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSelector } from "react-redux";
import Notificationlist from "./Notificationlist";
import OwnerDropdown from "./OwnerDropdown";
import ShipDropdown from "./ShipDropdown";
import Navbar from "./Navbar";
import GlobalSearch from "./Globalsearch";

const { Header } = Layout;

const MainHeader = ({ chartTitles }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const username = localStorage.getItem("username");
  const { logout, role } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const ship_id = localStorage.getItem("ship_id");
  const [showNotifications, setShowNotification] = useState(false);
  const { errorlist } = useSelector((state) => state.errorInfo);
  const { shipinfo } = useSelector((state) => state.ship);
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    localStorage.removeItem("serviceWorkerOffline");
  };

  const menu = (
    <Menu onClick={({ key }) => key === "logout" && handleLogout()}>
      <Menu.Item key="logout" icon={<LogOut size={16} />} danger>Logout</Menu.Item>
    </Menu>
  );

  const getRoleDisplayName = (role) => ({
    SUPERADMIN: "Super Admin", OWNER: "Owner",
    CHIEF_ENGINEER: "Chief Engineer", FLEET_MANAGER: "Fleet Manager",
    VESSEL_MANAGER: "Vessel Manager", OWNERGROUP: "Group Company",
    MASTER: "Master", ENGINE_ROOM: "Engine Room", BRIDGE: "Bridge User",
  }[role] || role);

  const MobileMenu = () => (
    <div style={{ padding: "20px" }}>
      <div style={{ marginBottom: 16 }}>
        <GlobalSearch chartTitles={chartTitles} />
      </div>
      <div style={{ textAlign: "center", marginBottom: 16 }}>
        <Typography.Text strong style={{ fontSize: "16px", color: "#0D9276" }}>
          {getRoleDisplayName(role)}
        </Typography.Text>
      </div>
      {(role === "FLEET_MANAGER" || role === "VESSEL_MANAGER" || role === "OWNERGROUP" || role === "OWNER") &&
        location.pathname !== "/management" && (
          <div style={{ marginBottom: 16 }}><ShipDropdown role={role} /></div>
        )}
      {role === "SUPERADMIN" && <div style={{ marginBottom: 16 }}><OwnerDropdown /></div>}
      {!["OWNER", "OWNERGROUP", "SUPERADMIN", "VESSEL_MANAGER", "FLEET_MANAGER"].includes(role) && (
        <div style={{ textAlign: "center" }}>
          <div
            className="d-flex justify-content-center align-items-center rounded-pill px-3 py-2"
            style={{ background: "linear-gradient(135deg,#0dd5a3,#0bae84)", display: "inline-flex", cursor: "pointer" }}
            onClick={() => navigate("/user/ship-info")}
          >
            <Typography.Text strong className="text-white me-2">{shipinfo?.name}</Typography.Text>
            <Ship className="text-white" size={20} />
          </div>
        </div>
      )}
    </div>
  );

  return (
    <React.Fragment>
      <Header style={{
        padding: "0 12px",
        backgroundColor: "rgba(255,255,255,0.97)",
        display: "flex",
        height: 54,
        alignItems: "center",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
        backdropFilter: "blur(10px)",
        zIndex: 200,
        gap: 0,
      }}>

        {/* ── LEFT: Logo + Brand ─────────────────────────────── */}
        <div style={{ display: "flex", alignItems: "center", flexShrink: 0, marginRight: 12 }}>
          <Avatar className="mb-2"
            style={{ width: 70, height: 55, flexShrink: 0 }}
            src={Logo}
          />
          <div className="ms-2 d-flex flex-row" style={{ marginLeft: 8, lineHeight: 1 }}>
            <Typography.Title
              level={4}
              style={{
                fontStyle: "italic", color: "#0D9276",
                margin: 0, lineHeight: 1, display: "block", whiteSpace: "nowrap",
              }}
            >
              SEA FLASH
            </Typography.Title>
            <Typography.Text  className="mt-1 ms-2" style={{ fontSize: 10, color: "#aaa" }}>(V2.9)</Typography.Text>
          </div>
        </div>

        {/* ── CENTER: Search (desktop) / Navbar for superadmin ── */}
        {role === "SUPERADMIN" ? (
          <div style={{ flex: 1, display: "flex", justifyContent: "center", minWidth: 0 }}>
            <Navbar />
          </div>
        ) : (
          /* Search — hidden on mobile, shown on md+ */
         <div
  className="d-none d-md-flex"
  style={{
    flex: 1,
    alignItems: "center",
    minWidth: 0,
    padding: "0 12px",
  }}
>
<div style={{ width: 420 }}>              <GlobalSearch chartTitles={chartTitles} />
            </div>
          </div>
        )}

        {/* ── RIGHT: Desktop controls ────────────────────────── */}
        <div className="d-none d-lg-flex align-items-center" style={{ flexShrink: 0, gap: 10 }}>
          {(role === "FLEET_MANAGER" || role === "VESSEL_MANAGER" || role === "OWNERGROUP" || role === "OWNER") &&
            location.pathname !== "/management" && <ShipDropdown role={role} />}
          {role === "SUPERADMIN" && <OwnerDropdown />}

          <span className="fw-semibold" style={{ fontSize: 14, whiteSpace: "nowrap" }}>
            {getRoleDisplayName(role)}
          </span>

          {!["OWNER", "OWNERGROUP", "SUPERADMIN", "VESSEL_MANAGER", "FLEET_MANAGER"].includes(role) && (
            <Tooltip title={ship_id}>
              <div
                className="d-flex align-items-center rounded-pill px-2 py-1"
                style={{
                  background: "linear-gradient(135deg,#0dd5a3,#0bae84)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                  cursor: "pointer",
                  gap: 6,
                }}
              >
                <Typography.Text strong className="text-white" style={{ fontSize: 12 }}>
                  {shipinfo?.name}
                </Typography.Text>
                <Ship
                  className="text-white"
                  size={16}
                  onClick={() => navigate("/user/ship-info")}
                />
              </div>
            </Tooltip>
          )}

          <Tooltip title="Notifications">
            <Badge size="small" count={errorlist?.length || 0} style={{ cursor: "pointer" }}>
              <Bell size={18} className="mx-2" onClick={() => setShowNotification(true)} style={{ cursor: "pointer" }} />
            </Badge>
          </Tooltip>

          <Dropdown overlay={menu} trigger={["click"]}>
            <div style={{ display: "flex", alignItems: "center", cursor: "pointer", gap: 4 }}>
              <User size={18} className="text-dark" />
              <span className="text-secondary fw-bold" style={{ fontSize: 13, whiteSpace: "nowrap" }}>
                {username}
              </span>
              <DownOutlined style={{ fontSize: 10, color: "#888" }} />
            </div>
          </Dropdown>
        </div>

        {/* ── RIGHT: Mobile controls ─────────────────────────── */}
        <div className="d-flex d-lg-none align-items-center ms-auto" style={{ gap: 8 }}>
          <Badge size="small" count={errorlist?.length || 0}>
            <Bell size={18} onClick={() => setShowNotification(true)} style={{ cursor: "pointer" }} />
          </Badge>
          <Dropdown overlay={menu} trigger={["click"]} onOpenChange={setOpen} open={open}>
            <User size={18} className="text-dark" style={{ cursor: "pointer" }} />
          </Dropdown>
          <Button
            type="text"
            size="small"
            icon={<MenuIcon size={18} />}
            onClick={() => setMobileMenuOpen(true)}
          />
        </div>
      </Header>

      {/* Mobile drawer */}
      <Drawer
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 600, color: "#0D9276" }}>Menu</span>
            <Button type="text" icon={<X size={18} />} onClick={() => setMobileMenuOpen(false)} />
          </div>
        }
        placement="right"
        closable={false}
        onClose={() => setMobileMenuOpen(false)}
        open={mobileMenuOpen}
        width={300}
      >
        <MobileMenu />
      </Drawer>

      <Notificationlist
        setShowNotification={setShowNotification}
        showNotifications={showNotifications}
      />
    </React.Fragment>
  );
};

export default MainHeader;